import React, { useRef, useState, useEffect } from 'react';
import { Card, CardContent, Typography, Button, Box, TextField, InputAdornment, CircularProgress, IconButton, Chip } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import Logo from "../../images/logo/Logo.svg";
import Uploadicon from "../../images/uploadicon.svg";
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API_CONFIG } from "../../api/apiUrl";
import toast, { Toaster } from 'react-hot-toast';

interface ServiceType {
  id: number;
  name: string;
  description: string;
  imgUrl: string;
  isDeleted: boolean;
  isActive: boolean;
}

const PlotDetails = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [plotSize, setPlotSize] = useState<string>('');
  const [plotLocation, setPlotLocation] = useState<string>('');
  const [plotFloors, setPlotFloors] = useState<string>('');
  const [plotRooms, setPlotRooms] = useState<string>('');
  const [files, setFiles] = useState<{ file: File; url: string; previewUrl: string }[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedService, setSelectedService] = useState<ServiceType | null>(null);
  const [remarks, setRemarks] = useState<string>('');

  useEffect(() => {
    if (location.state && location.state.selectedService) {
      setSelectedService(location.state.selectedService);
    }
  }, [location.state]);


  function handlePlotSizeChange(e:any) {
    setPlotSize(e.target.value.replace(/\D/g, ''));
  }
  function handlePlotSizeIncrement() {
    setPlotSize((prev) => (Number(prev) + 1).toString());
  }
  function handlePlotSizeDecrement() {
    setPlotSize((prev) => {
      const val = Number(prev) - 1;
      return val > 0 ? val.toString() : '';
    });
  }
  function handlePlotLocationChange(e:any) {
    setPlotLocation(e.target.value);
  }
  function handlePlotFloorsChange(e:any) {
    setPlotFloors(e.target.value.replace(/\D/g, ''));
  }
  function handlePlotRoomsChange(e:any) {
    setPlotRooms(e.target.value.replace(/\D/g, ''));
  }


  // Helper function to check if file is PDF
  const isPdfFile = (file: File) => {
    return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
  };

  // Helper function to format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Helper function to truncate filename
  const truncateFileName = (name: string, maxLength: number = 15) => {
    if (name.length <= maxLength) return name;
    const extension = name.split('.').pop();
    const nameWithoutExt = name.substring(0, name.lastIndexOf('.'));
    const truncatedName = nameWithoutExt.substring(0, maxLength - extension!.length - 4) + '...';
    return truncatedName + '.' + extension;
  };

  async function uploadFile(file: File): Promise<string | null> {
    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('id_token');
    try {
      const response = await axios.post(API_CONFIG.FILE_UPLOAD, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch {
      return null;
    }
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (!e.target.files) return;
    setIsLoading(true);
    const newFiles = Array.from(e.target.files);
    const uploadedFiles: { file: File; url: string; previewUrl: string }[] = [];
    for (const file of newFiles) {
      const url = await uploadFile(file);
      if (url) uploadedFiles.push({ file, url, previewUrl: URL.createObjectURL(file) });
    }
    setFiles((prev) => [...prev, ...uploadedFiles].slice(0, 4));
    setIsLoading(false);
  }

  function removeFile(index: number) {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }

  async function handleSubmit(e:any) {
    e.preventDefault();
    setIsLoading(true);
    const service = selectedService || location.state?.selectedService;
    const plotData = {
      plot: {
        plotSize: Number(plotSize),
        plotLocation,
        plotFloors: Number(plotFloors),
        plotFacing: '',
        plotRooms: Number(plotRooms),
        remarks: remarks.trim() ? `${remarks} (Service: ${service?.name || ''})` : `Service: ${service?.name || ''}`,
        contractorTypes: service ? { ...service } : null,
      },
      files: files.map(f => f.url),
    };
    try {
      navigate('/plot-requirements', {
        state: { initialData: plotData, selectedService: service },
      });
      toast.success('Plot details saved!');
    } catch (error: any) {
      alert('Error preparing data: ' + error.message);
      toast.error('Failed to save plot details');
    } finally {
      setIsLoading(false);
    }
  }

  const isButtonDisabled = !plotSize || !plotLocation || !plotFloors || !plotRooms || isLoading;

  return (
    <Box sx={styles.mainContainer}>
      <Toaster position="top-right" />
      <Card sx={styles.card}>
        <CardContent>
          <Box sx={styles.logoContainer}>
            <img src={Logo} alt="Logo" style={styles.logo} />
          </Box>
          <form onSubmit={handleSubmit}>
            {selectedService && (
              <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Selected Service</Typography>
                <Chip
                  label={selectedService.name}
                  color="primary"
                  sx={{
                    backgroundColor: '#89652A',
                    color: 'white',
                    fontWeight: 500,
                    fontSize: '16px',
                    padding: '20px 10px',
                    borderRadius: '20px',
                  }}
                />
              </Box>
            )}

            <Box>
              <Typography sx={styles.inputLabel}>Plot Size</Typography>
              <TextField
                fullWidth
                name="plotSize"
                placeholder="Enter Plot Size"
                variant="outlined"
                size="small"
                sx={styles.textField}
                onChange={handlePlotSizeChange}
                type="text"
                value={plotSize}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Box sx={styles.unitSelector}>
                        Sq Ft
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          <Box
                            sx={styles.upArrow}
                            onClick={handlePlotSizeIncrement}
                            component="span"
                            role="button"
                            aria-label="Increment Plot Size"
                          />
                          <Box
                            sx={styles.arrow}
                            onClick={handlePlotSizeDecrement}
                            component="span"
                            role="button"
                            aria-label="Decrement Plot Size"
                          />
                        </Box>
                      </Box>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box>
              <Typography sx={{ ...styles.inputLabel, mt: 2 }}>Plot Location</Typography>
              <TextField
                fullWidth
                name="plotLocation"
                placeholder="Enter Plot Location"
                variant="outlined"
                size="small"
                sx={styles.textField}
                onChange={handlePlotLocationChange}
                value={plotLocation}
              />
            </Box>
            <Box>
              <Typography sx={{ ...styles.inputLabel, mt: 2 }}>Number of Floors</Typography>
              <TextField
                fullWidth
                name="plotFloors"
                placeholder="Enter Number of Floors"
                variant="outlined"
                size="small"
                sx={styles.textField}
                onChange={handlePlotFloorsChange}
                type="text"
                value={plotFloors}
              />
            </Box>
            <Box>
              <Typography sx={{ ...styles.inputLabel, mt: 2 }}>Number of Rooms</Typography>
              <TextField
                fullWidth
                name="plotRooms"
                placeholder="Enter Number of Rooms"
                variant="outlined"
                size="small"
                sx={styles.textField}
                onChange={handlePlotRoomsChange}
                type="text"
                value={plotRooms}
              />
            </Box>
            <Typography sx={{ fontSize: '15px', fontWeight: 400, mb: 2, mt: 2.5 }}>
              Upload Existing Plans/ Designs
            </Typography>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              accept=".jpg,.jpeg,.png,.pdf"
              onChange={handleFileChange}
              multiple
            />
            <Box
              onClick={() => fileInputRef.current?.click()}
              sx={{ ...styles.uploadBox, border: '1px dashed #a57f4e', borderRadius: '12px', p: 3, textAlign: 'center', mb: 2, cursor: 'pointer' }}
            >
              <Box><img src={Uploadicon} alt="Logo" style={{ height: '50px' }} /></Box>
              <Typography sx={{ color: '#B69D74', fontSize: '15px', my: 1 }}>Upload your existing plans or designs</Typography>
              <Typography sx={{ fontSize: '14px', display: 'inline', my: 0.3 }}>
                <span style={{ textDecoration: 'underline', color: '#B69D74' }}>Browse Files</span> From Your Computer
              </Typography>
              <Typography sx={{ fontSize: '14px', my: 0.3 }}>Max size 10 MB, Format: .jpg, .png, .pdf, .jpeg</Typography>
            </Box>
            {files.length > 0 && (
              <Box sx={styles.imageGridContainer}>
                {files.map((fileObj, index) => (
                  <Box key={index} sx={styles.imageGridItem}>
                    {isPdfFile(fileObj.file) ? (
                      <Box sx={styles.pdfPreview}>
                        <Box sx={styles.pdfHeader}>
                          <PictureAsPdfIcon sx={{ fontSize: 20, color: '#DC4E41', mr: 1.5 }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography sx={styles.pdfFileName}>
                              {truncateFileName(fileObj.file.name, 20)}
                            </Typography>
                            <Typography sx={styles.pdfFileSize}>
                              {formatFileSize(fileObj.file.size)}
                            </Typography>
                          </Box>
                        </Box>
                      
                      </Box>
                    ) : (
                      <img src={fileObj.previewUrl} alt={`Uploaded ${index}`} style={styles.gridImage} />
                    )}
                    <IconButton sx={styles.removeImageButton} onClick={() => removeFile(index)}>
                      <CloseIcon sx={{ fontSize: 16, color: 'white' }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
            <Box sx={styles.submitContainer}>
              <Button type="submit" sx={styles.submitButton} disabled={isButtonDisabled}>
                {isLoading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Continue'}
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

const styles: any = {
  mainContainer: { my: { xs: 2, md: 7 }, display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: 1, md: 2 } },
  card: { boxShadow: "0px 0px 4px 0px #00000033", maxWidth: 500, width: '90%', p: 2, borderRadius: '20px' },
  logoContainer: { display: 'flex', justifyContent: 'center', mb: 3 },
  logo: { width: '130px' },
  textField: { '& .MuiOutlinedInput-root': { borderRadius: '50px', borderColor: '#89652A', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#89652A' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#89652A' } } },
  inputLabel: { fontSize: '15px', fontWeight: 400, mb: 1.5, color: '#212121' },
  submitContainer: { display: 'flex', justifyContent: 'center', mt: 3 },
  submitButton: { textTransform: 'capitalize', fontSize: { xs: '16px', md: '16px' }, fontWeight: 600, borderRadius: '28px', color: 'white', bgcolor: '#89652A', py: 0.7, px: { xs: 4, md: 6 }, '&:hover': { bgcolor: '#6b4e20' }, '&:disabled': { bgcolor: '#a88b5e' } },
  unitSelector: { height: '100%', display: 'flex', alignItems: 'center', padding: '8px', borderRadius: '25px', color: '#BDBDBD', fontSize: '15px', borderLeft: '1px solid #B69D74', gap: '4px', cursor: 'pointer', marginRight: '-1px' },
  arrow: { width: 5, height: 5, marginLeft: '5px', color: '#BDBDBD', borderLeft: '1.5px solid #BDBDBD', borderBottom: '1.5px solid #BDBDBD', transform: 'rotate(-45deg)', marginTop: '-1px', display: 'inline-block' },
  upArrow: { width: 5, height: 5, marginLeft: '5px', color: '#BDBDBD', borderLeft: '1.5px solid #BDBDBD', borderBottom: '1.5px solid #BDBDBD', transform: 'rotate(135deg)', marginBottom: '-1px', display: 'inline-block' },
  uploadBox: { transition: 'all 0.2s ease', '&:hover': { bgcolor: '#f8f8f8', borderColor: '#89652A' } },
  imageGridContainer: { display: 'grid', gridTemplateColumns: 'repeat(1, 1fr)', gap: 1.5, mb: 3, position: 'relative' },
  imageGridItem: { position: 'relative', borderRadius: '8px', overflow: 'hidden', minHeight: '60px', bgcolor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  gridImage: { width: '100%', height: '100%', objectFit: 'cover' },
  pdfPreview: { 
    display: 'flex', 
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    height: '60px',
    bgcolor: '#ffffff',
    border: '1px solid #e5e5e5',
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
    p: 1.5
  },
  pdfHeader: {
    display: 'flex',
    alignItems: 'center',
    flex: 1
  },
  pdfFileName: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#1f2937',
    lineHeight: 1.2,
    mb: 0.3
  },
  pdfFileSize: {
    fontSize: '11px',
    color: '#6b7280',
    lineHeight: 1
  },
  pdfFooter: {
    bgcolor: '#DC4E41',
    py: 0.5,
    px: 1,
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ml: 1
  },
  pdfLabel: {
    fontSize: '10px',
    color: 'white',
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.3px'
  },
  removeImageButton: { position: 'absolute', top: 5, right: 5, bgcolor: 'rgba(0,0,0,0.5)', width: 24, height: 24, '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } },
};

export default PlotDetails;